import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isImpersonating: boolean;
  impersonatedUserId: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  impersonateUser: (userId: string) => Promise<{ error: Error | null }>;
  exitImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);
  const [adminSession, setAdminSession] = useState<Session | null>(null);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: fullName ? { full_name: fullName } : {}
      }
    });
    return { error: error as Error | null };
  };

  const signInWithGoogle = async () => {
    try {
      // Get the OAuth URL without auto-redirecting
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          skipBrowserRedirect: true,
        }
      });

      if (error) {
        return { error: error as Error | null };
      }

      if (!data?.url) {
        return { error: new Error("No OAuth URL returned") as Error };
      }

      // Open OAuth in popup window
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        data.url,
        "google-signin",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no`
      );

      if (!popup) {
        // Popup blocked - fallback to redirect
        window.location.href = data.url;
        return { error: null };
      }

      // Monitor popup for completion
      return new Promise<{ error: Error | null }>((resolve) => {
        const checkPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkPopup);
            // The auth state change listener will handle the session
            resolve({ error: null });
          }
        }, 500);

        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(checkPopup);
          if (!popup.closed) {
            popup.close();
          }
          resolve({ error: new Error("Authentication timeout") as Error });
        }, 300000);
      });
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  };

  const impersonateUser = async (userId: string) => {
    try {
      // Store current admin session
      if (session && !isImpersonating) {
        setAdminSession(session);
      }

      // Log the impersonation in audit logs
      await supabase.from("audit_logs").insert({
        user_id: user?.id,
        action: "impersonate_user",
        resource_type: "user",
        resource_id: userId,
        details: { impersonated_user_id: userId },
        level: "info",
      });

      setIsImpersonating(true);
      setImpersonatedUserId(userId);

      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const exitImpersonation = () => {
    setIsImpersonating(false);
    setImpersonatedUserId(null);
    setAdminSession(null);

    // Log the exit
    if (user?.id && impersonatedUserId) {
      supabase.from("audit_logs").insert({
        user_id: user.id,
        action: "exit_impersonation",
        resource_type: "user",
        resource_id: impersonatedUserId,
        details: { impersonated_user_id: impersonatedUserId },
        level: "info",
      });
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      isImpersonating,
      impersonatedUserId,
      signIn,
      signUp,
      signOut,
      signInWithGoogle,
      resetPassword,
      impersonateUser,
      exitImpersonation
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
